import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { auditLogKeys } from '@/api/audit-logs';
import { partnerAccountKeys } from '@/api/partner-accounts';
import { syncLogKeys } from '@/api/sync-logs';
import { syncedLocationKeys } from '@/api/synced-locations';
import type { SyncStatus } from '@/types/api';

interface UseSyncStatusInvalidationOptions {
  /** Account ID to invalidate sync logs for (optional - for detail pages) */
  accountId?: string;
  /** Whether to invalidate the accounts list */
  invalidateList?: boolean;
}

/**
 * Watches sync status changes and invalidates relevant queries when sync completes.
 *
 * This is more efficient than continuous polling because:
 * - We only poll the account(s) during active sync to detect completion
 * - Related queries (sync logs, etc.) are invalidated once when status changes
 * - No need to poll sync logs or other data continuously
 *
 * @param syncStatus - Current sync status (single account)
 * @param options - Configuration options
 */
export function useSyncStatusInvalidation(
  syncStatus: SyncStatus | undefined,
  options: UseSyncStatusInvalidationOptions = {}
): void {
  const { accountId, invalidateList = true } = options;
  const queryClient = useQueryClient();
  const prevStatusRef = useRef<SyncStatus | undefined>(syncStatus);

  useEffect(() => {
    const prevStatus = prevStatusRef.current;

    // Detect transition from 'syncing' to any other status
    if (prevStatus === 'syncing' && syncStatus !== 'syncing' && syncStatus !== undefined) {
      // Sync just completed - invalidate related queries
      if (invalidateList) {
        void queryClient.invalidateQueries({ queryKey: partnerAccountKeys.all });
      }

      if (accountId) {
        // Invalidate sync logs for this account
        void queryClient.invalidateQueries({
          queryKey: syncLogKeys.list(accountId, undefined),
          exact: false
        });
      }

      // Invalidate synced locations and stats since sync may have added/updated locations
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.all });

      // Invalidate audit logs since sync creates audit entries
      void queryClient.invalidateQueries({ queryKey: auditLogKeys.all });
    }

    prevStatusRef.current = syncStatus;
  }, [syncStatus, accountId, invalidateList, queryClient]);
}

/**
 * Watches multiple accounts for sync status changes.
 * Useful for list pages where any account may be syncing.
 *
 * @param accounts - Array of accounts with syncStatus
 */
export function useSyncStatusInvalidationForList(
  accounts: { id: string; syncStatus: SyncStatus }[] | undefined
): void {
  const queryClient = useQueryClient();
  const prevStatusMapRef = useRef<Map<string, SyncStatus>>(new Map());

  useEffect(() => {
    if (!accounts) return;

    const prevStatusMap = prevStatusMapRef.current;
    let shouldInvalidateList = false;

    for (const account of accounts) {
      const prevStatus = prevStatusMap.get(account.id);

      // Detect transition from 'syncing' to any other status
      if (prevStatus === 'syncing' && account.syncStatus !== 'syncing') {
        shouldInvalidateList = true;

        // Also invalidate sync logs for this specific account
        void queryClient.invalidateQueries({
          queryKey: syncLogKeys.list(account.id, undefined),
          exact: false
        });
      }

      prevStatusMap.set(account.id, account.syncStatus);
    }

    if (shouldInvalidateList) {
      void queryClient.invalidateQueries({ queryKey: partnerAccountKeys.all });
      // Invalidate synced locations and stats since sync may have added/updated locations
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.all });
      // Invalidate audit logs since sync creates audit entries
      void queryClient.invalidateQueries({ queryKey: auditLogKeys.all });
    }
  }, [accounts, queryClient]);
}
